import { Checkbox } from "@commercekit/core";
import { typedBoolean } from "@commercekit/shared";
import { withZod } from "@remix-validated-form/with-zod";
import { z } from "zod";
import { Country, numberValidation } from "./utils";

/**
 * VALIDATOR OPTIMIZATIONS SUMMARY:
 *
 * 1. Added utility functions for common validation patterns:
 *    - createPaymentMethodValidation(): For reusable payment method validation
 *    - createBusinessValidationRefinements(): For B2B business logic validations
 *
 * 2. Better organized validation schemas by grouping related validators
 *
 * 3. Added documentation and comments for clarity
 *
 * 4. Improved type safety and reduced code duplication
 */

export const required = (msg: string) => z.string().trim().min(1, msg);
export const requiredMin = (minlength: number, requiredMsg: string, minimumMsg: string) =>
  z.string().trim().min(1, requiredMsg).min(minlength, minimumMsg);

// Flexible validator for optional or dynamic fields
export const anyZ = z.any();

/**
 * Creates a payment method validation refinement that can be used with Zod schemas
 * @returns An object with validation function, message, and path
 */
export const createPaymentMethodValidation = () => ({
  validate: (data: any) => data.paymentMethods,
  message: "payment_method_required_message",
  path: ["paymentMethods"] as const,
});

// === VALIDATION SCHEMAS AND UTILITIES ===

export interface PasswordValidator {
  PASSWORD_LENGTH_MIN: string;
  PASSWORD_REQUIREMENTS: string;
}
const passwordZ = ({ PASSWORD_LENGTH_MIN }: PasswordValidator) =>
  z.string().min(1, "form_message_password_required").min(Number(PASSWORD_LENGTH_MIN), "form_message_password_strength");

const confirmPasswordZ = z.string();
const passwordWithRegex = (passwordValidator: PasswordValidator) =>
  passwordZ(passwordValidator).regex(
    /^((?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])|(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_])|(?=.*[a-z])(?=.*[0-9])(?=.*[\W_])|(?=.*[A-Z])(?=.*[0-9])(?=.*[\W_])).{8,}$/,
    "form_message_password_invalid"
  );

// PASSWORD REGEX TO BE SAVED ON .ENV LATER

const computeVatNumber = (selectedLocale, length, max = null) =>
  z
    .string()
    .min(length, "invalid_vat_structure")
    .max(max ?? length, "invalid_vat_structure")
    .startsWith(selectedLocale, "invalid_vat_structure")
    .regex(new RegExp("^[a-zA-Z0-9]+$"), "invalid_vat_structure");

const computeVatNumberZ = (selectedLocale) => {
  switch (selectedLocale) {
    case "NL":
      return computeVatNumber(selectedLocale, 14).refine(
        (val) => {
          const check = val.toUpperCase().slice(0, val.length - 2);
          return check.endsWith("B");
        },
        { message: "invalid_vat_structure" }
      );

    case "BE":
    case "PL":
      return computeVatNumber(selectedLocale, 12);

    case "FR":
    case "IT":
      return computeVatNumber(selectedLocale, 13);

    case "DE":
    case "ES":
    case "PT":
      return computeVatNumber(selectedLocale, 11);

    case "AT":
      return computeVatNumber(`${selectedLocale}U`, 11);

    case "GB":
      return computeVatNumber("GB", 10, 11);
    // return computeVatNumber("GB", 8).regex(new RegExp("^(OC|LP)d*|d+$"), "invalid_vat_structure");

    case "IE":
      return computeVatNumber("IE", 10, 11);

    case "DK":
      return computeVatNumber(selectedLocale, 10);

    case "SE":
      return computeVatNumber(selectedLocale, 14);
  }
};

export const isCompanyZ = (vatIsRequired, selectedLocale) => ({
  isB2b: z.any(),
  companyName: z.any(),
  kvkNumber: z.any(),
  branch: z.any(),
  vatNumber: vatIsRequired ? computeVatNumberZ(selectedLocale) : z.any(),
});

export const deliveryLocationZ = z.any().refine(
  (val) => {
    return typeof val === "string" && val.length > 1;
  },
  { message: "form_message_location_required" }
);
const emailZ = required("form_message_email_required").email("form_message_email_invalid");
// 100 chars: address1 holds the street name only (house number + additions go
// to the separate address2 field). The backend imposes no limit — NetSuite's
// AddressLine1 is an unconstrained string, Marten stores JSONB, and the only
// downstream ceiling is BigCommerce's 255-char address line. The old 32 cap was
// an arbitrary UI value (copied from the name fields) that silently blocked
// legitimate long streets (e.g. FR/BE addresses). 100 covers real-world streets
// with margin while staying well under the 255 platform limit.
const address1Z = required("form_message_address1_required").max(100, "address_maximum_char_limit");
const cityZ = required("form_message_city_required");
const countryCodeZ = required("form_message_country_required");
const postalCodeZ = required("form_message_postal_code_required");
const firstNameZ = required("form_message_first_name_required")
  .min(2, "name_minimum_char_limit")
  .max(32, "name_maximum_char_limit")
  .regex(/^(?!.*[-' ]{2})[\p{L}'\- ]+$/u, "form_message_name_invalid_format");
const lastNameZ = required("form_message_last_name_required")
  .min(2, "name_minimum_char_limit")
  .max(32, "name_maximum_char_limit")
  .regex(/^(?!.*[-' ]{2})[\p{L}'\- ]+$/u, "form_message_name_invalid_format");
const phoneZ = required("form_message_phone_required").regex(/^\+[1-9]\d{3,14}$/, "form_message_phone_invalid_format");

const messageZ = required("form_message_content_required");
const topicZ = required("form_message_topic_required");
const informationZ = required("form_message_information_required");
const orderCodeZ = required("form_message_order_status_code");
const houseNumberZ = required("form_message_house_number_required");
const streetNameZ = required("form_message_street_name_required");
const placeZ = required("form_message_place_required");

const nameZ = required("form_message_name_required");
const addrZ = required("form_message_address_required").max(100, "address_maximum_char_limit");

const contactFormTypeZ = required("contact_form_type_error_message");
const contactNameZ = required("contact_name_error_message");
const contactCompanyNameZ = required("form_message_field_required");
const contactArticleCodeZ = required("form_message_field_required");
const contactEmailZ = required("contact_email_error_message").email("form_message_email_invalid");
const contactPhoneZ = required("contact_phone_error_message");
const contactOrderNumberZ = required("form_message_field_required");
const contactCommentZ = required("contact_comment_error_message");

// const referenceZ = required("form_message_field_required");
const companyNameZ = required("form_message_field_required");
// NetSuite caps vatregnumber at 20 chars; longer values dead-letter the order on ERP sync.
export const VAT_NUMBER_MAX_LENGTH = 20;
const vatNumberZ = z
  .string()
  .max(VAT_NUMBER_MAX_LENGTH, "vat_number_maximum_char_limit")
  .trim()
  .min(1, "form_message_field_required");
const kvkNumberZ = required("form_message_field_required");

const loginZ = (passwordValidator: PasswordValidator) => ({
  email: emailZ,
  password: passwordZ(passwordValidator),
});
export const signupZ = (passwordValidator: PasswordValidator, passwordOnly: boolean = false) => {
  const baseFields = {
    password: passwordWithRegex(passwordValidator),
    confirmPassword: confirmPasswordZ,
  };

  if (passwordOnly) {
    return baseFields;
  }

  return {
    email: emailZ,
    ...baseFields,
    subNewsletter: z.any(),
  };
};
export const addressKey = (pre: string, key: string) => [pre, key].filter(typedBoolean).join("_");
export const addressZ = (pre: "billing" | "shipping" | "", withHouseNumber: boolean = true, isManualAddress: boolean = false) => {
  return {
    [addressKey(pre, `email`)]: pre === "billing" ? emailZ : anyZ,
    [addressKey(pre, `countryCode`)]: countryCodeZ,
    [addressKey(pre, `postalCode`)]: isManualAddress ? postalCodeZ : anyZ,
    [addressKey(pre, `city`)]: isManualAddress ? cityZ : anyZ,
    [addressKey(pre, `address1`)]: isManualAddress ? address1Z : anyZ,
    [addressKey(pre, `houseNumber`)]: isManualAddress && withHouseNumber ? houseNumberZ : anyZ,
    [addressKey(pre, `firstName`)]: firstNameZ,
    [addressKey(pre, `lastName`)]: lastNameZ,
    [addressKey(pre, `phone`)]: pre === "billing" ? required("form_message_phone_required") : anyZ,
    phoneCountryCode: anyZ,
  };
};

export const loginValidator = (passwordValidator: PasswordValidator) => withZod(z.object(loginZ(passwordValidator)));

export const registerInputZ = (withHouseNumber: boolean) => ({
  [`email`]: emailZ,
  [`address1`]: address1Z,
  [`city`]: cityZ,
  [`countryCode`]: countryCodeZ,
  [`postalCode`]: postalCodeZ,
  [`firstName`]: firstNameZ,
  [`lastName`]: lastNameZ,
  [`phone`]: anyZ,
  [`houseNumber`]: withHouseNumber ? houseNumberZ : anyZ,
});
export const registerValidatorZ = (
  passwordValidator: PasswordValidator,
  withHouseNumber: boolean = true,
  kvkIsRequired: boolean = true,
  vatIsRequired: boolean = true,
  selectedLocale: string = "",
  phoneCodeCountries: Country[] = []
) =>
  withZod(
    z
      .object({
        ...registerInputZ(withHouseNumber),
        ...signupZ(passwordValidator),
        ...isCompanyZ(vatIsRequired, selectedLocale),
      })
      .superRefine((data, ctx) => {
        // Phone required + country-aware validation
        const phone = data["phone"];
        if (!phone || (typeof phone === "string" && phone.trim().length === 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "form_message_field_required",
            path: ["phone"],
          });
          return;
        }

        const selectedCountry = phoneCodeCountries.find((c) => c.country === data.countryCode);
        const { isValid, message } = numberValidation(selectedCountry, phone);
        if (!isValid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: ["phone"],
          });
        }
      })
      .refine((data) => data.password === data.confirmPassword, {
        message: "form_message_confirm_password_not_match",
        path: ["confirmPassword"],
      })
      .refine(
        (data) =>
          data.isB2b === Checkbox.YES && kvkIsRequired && selectedLocale === "GB"
            ? /^(OC|LP)\d{6}$|^\d{8}$/.test(data.kvkNumber)
            : true,
        {
          message: "checkout_message_invalid_coc_number",
          path: ["kvkNumber"],
        }
      )
      .refine((data) => (data.isB2b === Checkbox.YES ? data.vatNumber : true), {
        message: "form_message_field_required",
        path: ["vatNumber"],
      })
      .refine((data) => (data.isB2b === Checkbox.YES ? data.branch : true), {
        message: "form_message_field_required",
        path: ["branch"],
      })
      .refine((data) => (data.isB2b === Checkbox.YES ? data.companyName : true), {
        message: "form_message_field_required",
        path: ["companyName"],
      })
  );

export const customerAddressZ = {
  address1: address1Z,
  city: cityZ,
  countryCode: countryCodeZ,
  postalCode: postalCodeZ,
  firstName: firstNameZ,
  lastName: lastNameZ,
};

export const businessInfoZ = (pre: "billing" | "shipping" | "", withKvk) => {
  return {
    // [addressKey(pre, `reference`)]: referenceZ,
    [addressKey(pre, `companyName`)]: companyNameZ,
    [addressKey(pre, `vatNumber`)]: vatNumberZ,
    [addressKey(pre, `purchaseReference`)]: z.string().max(32, "form_message_maximum_char_limit"),
    ...(withKvk ? { [addressKey(pre, `kvkNumber`)]: kvkNumberZ } : {}),
  };
};

/**
 * Utility function to create common business validation refinements
 */
export const createBusinessValidationRefinements = (isB2b: boolean, kvkIsRequired: boolean, selectedLocale: string) => [
  // KVK number validation for B2B
  {
    condition: (data: any) => (isB2b && kvkIsRequired ? data.kvkNumber : true),
    message: "form_message_field_required",
    path: ["kvkNumber"],
  },
  // GB specific KVK validation
  {
    condition: (data: any) =>
      isB2b && kvkIsRequired && selectedLocale === "GB" ? /^(OC|LP)\d{6}$|^\d{8}$/.test(data.kvkNumber) : true,
    message: "checkout_message_invalid_coc_number",
    path: ["kvkNumber"],
  },
  // VAT number validation for B2B
  {
    condition: (data: any) => (isB2b ? data.vatNumber : true),
    message: "form_message_field_required",
    path: ["vatNumber"],
  },
  // Branch validation for B2B
  {
    condition: (data: any) => (isB2b ? data.branch : true),
    message: "form_message_field_required",
    path: ["branch"],
  },
  // Company name validation for B2B
  {
    condition: (data: any) => (isB2b ? data.companyName : true),
    message: "form_message_field_required",
    path: ["companyName"],
  },
];

export const addressValidator = withZod(z.object(customerAddressZ));

export const changePasswordValidator = (passwordValidator: PasswordValidator) =>
  withZod(
    z
      .object({
        currentPassword: z.string().min(1, "form_message_password_required"),
        password: passwordWithRegex(passwordValidator),
        confirmPassword: confirmPasswordZ,
      })
      .refine((data) => data.password === data.confirmPassword, {
        message: "form_message_confirm_password_not_match",
        path: ["confirmPassword"],
      })
  );

export const updateCustomerValidator = withZod(
  z.object({
    firstName: firstNameZ,
    lastName: lastNameZ,
    email: emailZ,
  })
);

export const updateCustomerNameValidator = withZod(
  z.object({
    firstName: required("form_message_field_required"),
    lastName: required("form_message_field_required"),
  })
);

export const updateCustomerEmailValidator = withZod(
  z.object({
    email: required("form_message_field_required").email("form_message_email_invalid"),
  })
);

export const updateCustomerPhoneValidator = withZod(
  z.object({
    phone: phoneZ,
  })
);

export const updateCustomerAddressValidator = (useHouseHumber: boolean = true) =>
  withZod(
    z.object({
      // postalCode: required("form_message_field_required").regex(
      //   /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i,
      //   "form_message_postal_code_invalid_format"
      // ),
      postalCode: required("form_message_field_required"),
      city: required("form_message_field_required"),
      address1: required("form_message_field_required"),
      streetNumber: useHouseHumber ? required("form_message_field_required") : anyZ,
      additionalStreetInfo: anyZ,
      // address2: anyZ,
      countryCode: anyZ,
    })
  );

export const updateNonMandatoryField = (name: string) =>
  withZod(
    z.object({
      [name]: anyZ,
    })
  );

export const resetPwdValidator = withZod(
  z.object({
    email: emailZ,
  })
);
export const createOrderValidator = (showRegister: boolean, passwordValidator: PasswordValidator) =>
  withZod(
    z
      .object({
        terms: z.any(),
        branch: z.any(),
        ...(showRegister ? { password: passwordWithRegex(passwordValidator), confirmPassword: confirmPasswordZ } : {}),
        isBusinessAccount: z.any(),
      })
      .refine((data) => data.terms, {
        message: "checkout_label_agreement_required",
        path: ["terms"],
      })
      .refine((data) => (showRegister ? data.password === data.confirmPassword : true), {
        message: "form_message_confirm_password_not_match",
        path: ["confirmPassword"],
      })
      .refine((data) => (showRegister && data.isBusinessAccount ? data.branch : true), {
        message: "form_message_field_required",
        path: ["branch"],
      })
  );

export const newsletterValidator = updateCustomerValidator;
export const resetPasswordValidator = (passwordValidator: PasswordValidator) =>
  withZod(
    z
      .object({
        password: passwordWithRegex(passwordValidator),
        confirmPassword: confirmPasswordZ,
      })
      .refine((data) => data.password === data.confirmPassword, {
        message: "form_message_confirm_password_not_match",
        path: ["confirmPassword"],
      })
  );

export const contactUsValidator = withZod(
  z.object({
    firstName: firstNameZ,
    lastName: lastNameZ,
    email: emailZ,
    phone: phoneZ,
    message: messageZ,
  })
);

export const contactUsWithMapValidator = withZod(
  z.object({
    subject: topicZ,
    firstName: firstNameZ,
    lastName: lastNameZ,
    email: emailZ,
    phone: phoneZ,
    message: informationZ,
  })
);

export const contactUsWithRichTextValidator = withZod(
  z.object({
    firstName: nameZ,
    address2: houseNumberZ,
    postalCode: postalCodeZ,
    address1: addrZ,
    email: emailZ,
    message: messageZ,
  })
);

const baseWovarContactUsFields = {
  formType: contactFormTypeZ,
  name: contactNameZ,
  email: contactEmailZ,
  phone: contactPhoneZ,
  comment: contactCommentZ,
};
export const baseWovarContactUsValidator = withZod(z.object(baseWovarContactUsFields));

export const orderWovarContactUsValidator = withZod(
  z.object({
    ...baseWovarContactUsFields,
    orderNumber: contactOrderNumberZ,
  })
);

export const b2bWovarContactUsValidator = withZod(
  z.object({
    ...baseWovarContactUsFields,
    companyName: contactCompanyNameZ,
  })
);

export const productWovarContactUsValidator = withZod(
  z.object({
    ...baseWovarContactUsFields,
    articleCode: contactArticleCodeZ,
  })
);

export const campaignPhotoUploadValidator = withZod(
  z.object({
    name: required("campaign_name_error_message"),
    email: z.string().min(1, "campaign_email_required_error_msg").email("campaign_email_invalid_error_msg"),
  })
);

export const orderStatusValidator = (postcodeRule: string | null) => {
  let postcodeRuleRex: RegExp | null = null;
  if (postcodeRule) {
    try {
      postcodeRuleRex = new RegExp(postcodeRule);
    } catch (e) {}
  }
  return withZod(
    z.object({
      postcode: postcodeRuleRex ? postalCodeZ.regex(postcodeRuleRex, "form_message_postal_code_invalid_format") : postalCodeZ,
      code: orderCodeZ,
    })
  );
};
